/**
 * The player's input (spec 012): `toPlayableScene` turns a stored or
 * published scene into a `PlayableScene`. Pure and idempotent; spec 020
 * applies it to every scene `StoryRuntime` loads and every scene story
 * publish writes.
 */

import type {
  Background,
  ChoiceOption,
  Dialog,
  Keyframe,
  KeyframeTrack,
  Orientation,
  PlayableScene,
  Scene,
  SheetCut,
  Signal,
  Sound,
  Sprite,
} from '../@types';
import { mergeDerivedKeys, resolveLineExpressionKeys } from '../utils/playbackKeyframes';
import { resolveSceneLength } from '../utils/sceneTiming';
import { validateBackground } from '../utils/backgroundStates';
import { DEFAULT_BACKGROUND_COLOR, stripRetiredSceneFields } from '../utils/sceneContentDefaults';

/** What `toPlayableScene` accepts: a manifest `Scene`, an editor `SceneData`, or a `PlayableScene`. */
export type PlayableSceneInput = Omit<Partial<Scene>, 'id' | 'duration'> & {
  id: string | number;
  duration?: number | null;
  keyframes?: KeyframeTrack[] | null;
};

export interface PlayableSceneOptions {
  /**
   * Spritesheet cuts by media id (spec 033 D-3), from whoever knows the
   * media: the Service at publish, the Editor for its renderers. Those of the
   * media the scene's states use become `sheets`; without this option the
   * input's own `sheets` (a published scene's) are kept.
   */
  sheets?: Record<number, SheetCut | undefined> | null;
}

export const PLAYABLE_ORIENTATIONS: readonly Orientation[] = ['portrait', 'landscape', 'desktop'];

const list = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

/**
 * The background as it plays. States that `validateBackground` refuses (a
 * list with an empty, too long or repeated name) are dropped with their
 * default, so the background plays its `baseMediaId` as one with no states
 * (spec 039 D-1); an unknown `defaultState` plays the first state.
 */
function playableBackground(bg: Background | undefined | null): Background {
  if (!bg) return { id: 'background', name: 'Background', color: DEFAULT_BACKGROUND_COLOR, layers: [] };
  const out: Background = { ...bg, layers: list(bg.layers) };
  if (out.states !== undefined && validateBackground({ states: out.states }) !== undefined) {
    delete out.states;
    delete out.defaultState;
  }
  return out;
}

/** Ids of the line-expression keys `toPlayableScene` adds (and drops from its input). */
const isDerivedKey = (kf: Keyframe | null | undefined) => typeof kf?.id === 'string' && kf.id.startsWith('expr-');

/**
 * The scene's tracks with its line-expression `state` keys merged in by
 * spec 006's precedence rule (`resolveLineExpressionKeys`,
 * `mergeDerivedKeys`), in a form that plays right in every orientation: the
 * keys as desktop resolves them, untagged (they count wherever the untagged
 * keys do); and, for portrait and landscape, where that orientation resolves
 * to tagged keys (the sprite's authored `state` keys are tagged with it),
 * those keys too, ids suffixed `-<orientation>`. `authored` holds no
 * `expr-` key. A sprite with line keys but no track gets one, after the
 * authored tracks.
 */
function withLineExpressionKeys(scene: PlayableScene, authored: KeyframeTrack[]): KeyframeTrack[] {
  const base = { sprites: scene.sprites, dialogs: scene.dialogs, keyframes: authored };
  const derived = new Map<string, Keyframe[]>();
  const add = (keys: Map<string, Keyframe[]>, suffix: (kf: Keyframe) => Keyframe | null) => {
    keys.forEach((kfs, spriteId) => {
      const kept = kfs.map(suffix).filter((k): k is Keyframe => !!k);
      if (kept.length > 0) derived.set(spriteId, [...(derived.get(spriteId) ?? []), ...kept]);
    });
  };
  add(resolveLineExpressionKeys(base, 'desktop'), (kf) => kf);
  for (const o of ['portrait', 'landscape'] as const) {
    add(resolveLineExpressionKeys(base, o), (kf) => (kf.orientation === o ? { ...kf, id: `${kf.id}-${o}` } : null));
  }
  if (derived.size === 0) return authored;
  // A stable sort by time keeps each orientation's own order (an end key
  // before a start key at the same time); the orientations never compete.
  derived.forEach((kfs) => kfs.sort((a, b) => a.time - b.time));
  const out = authored.map((track) => {
    const keys = derived.get(track.trackId);
    return keys ? { ...track, keyframes: mergeDerivedKeys(track.keyframes, keys) } : track;
  });
  for (const sprite of scene.sprites) {
    const keys = derived.get(sprite.id);
    if (keys && !authored.some((t) => t.trackId === sprite.id)) out.push({ trackId: sprite.id, keyframes: keys });
  }
  return out;
}

/**
 * The cuts of the media the sprites' states use, by media id; undefined when
 * none has one, so a scene without sheets carries no `sheets` key (spec 033).
 */
function sceneSheets(sprites: Sprite[], all: PlayableSceneOptions['sheets']): Record<number, SheetCut> | undefined {
  if (!all || typeof all !== 'object') return undefined;
  const out: Record<number, SheetCut> = {};
  let any = false;
  for (const sprite of sprites) {
    for (const state of sprite?.states ?? []) {
      const id = state?.mediaId;
      if (typeof id !== 'number' || !(id > 0)) continue;
      const cut = (all as Record<number, SheetCut | undefined>)[id];
      if (cut && typeof cut === 'object') {
        out[id] = cut;
        any = true;
      }
    }
  }
  return any ? out : undefined;
}

/**
 * The scene as the player takes it:
 *
 * - whatever Storyboard left (its mode field, its sprite fields) is removed
 *   (`stripRetiredSceneFields`, spec 006); nothing branches on a mode and
 *   keyframe values are always absolute;
 * - the keyframes are the authored ones plus the line-expression `state`
 *   keys (`withLineExpressionKeys`); any `expr-` key in the input is dropped
 *   first, and a track left empty by that goes with it;
 * - missing arrays (`sprites`, `dialogs`, `sounds`, `signals`, `keyframes`,
 *   `choices`, background `layers`) become `[]`, a missing background the
 *   default one, and the id a string;
 * - `sheets` holds the spritesheet cuts (spec 033) of the media its states
 *   use, from `opts.sheets` or else the input's own; absent when none;
 * - `duration` becomes `resolveSceneLength` of the result.
 *
 * Applying it twice gives the same result.
 */
export function toPlayableScene(input: PlayableSceneInput, opts: PlayableSceneOptions = {}): PlayableScene {
  // A standalone scene's picture map (spec 030) is authoring data: it never plays.
  // What Storyboard left (its mode field, sprite animation fields) never plays (spec 006 v5).
  const { pictureMap: _pictureMap, sheets: ownSheets, ...rest } = stripRetiredSceneFields(input) as PlayableSceneInput & { pictureMap?: unknown; sheets?: unknown };
  const authored = list<KeyframeTrack>(rest.keyframes).flatMap((t) => {
    const all = list(t.keyframes);
    const keyframes = all.filter((kf) => !isDerivedKey(kf));
    return keyframes.length === 0 && all.length > 0 ? [] : [{ ...t, keyframes }];
  });
  const scene = {
    ...rest,
    id: String(input.id),
    name: input.name ?? '',
    background: playableBackground(input.background),
    sprites: list<Sprite>(rest.sprites),
    dialogs: list<Dialog>(input.dialogs),
    sounds: list<Sound>(input.sounds),
    signals: list<Signal>(input.signals),
    keyframes: authored,
    choices: list<ChoiceOption>(input.choices),
    duration: 0,
  } as PlayableScene;

  const sheets = sceneSheets(scene.sprites, opts.sheets ?? (ownSheets as PlayableSceneOptions['sheets']));
  if (sheets) scene.sheets = sheets;

  scene.keyframes = withLineExpressionKeys(scene, authored);
  scene.duration = resolveSceneLength({ ...scene, duration: input.duration });
  return scene;
}
